import React, { useEffect, useMemo, useReducer } from "react";
import { useHistory } from "react-router-dom";

import WeAreSorry from "components/weAreSorry";
import Button from "components/button";
import { API_FETCH_STATUS, PARTY_AGGREGATION_LEVEL } from "constants/common";
import { generateCancelToken, isRequestCancelled } from "interceptor/aeh";
import MergeStateReducer from "utils/mergeStateReducer";
import { encryptObject } from "utils/encryptObject";
import Environment from "services/environment/index";
import { navigateHandler } from "utils/redirection";
import { LINK_TO_ROUTE, PROFILE_TYPES } from "constants/common";
import usePartyAccounts from "hooks/usePartyAccounts";

import Loader from "../loader/loader";
import Filters from "../filters/filters";
import AccordionView from "../accordianView/accordionView";
import PartyListContainer from "../partiesListContainer/partiesListContainer";
import PartyInput from "../partyInput/partyInput";
import { getSearchTypeOptions } from "../utils/utils";
import { RAISE_SR_ATB_ACCOUNT_ISSUE } from "../constants/constants";

const env = Environment.getEnv();

import styles from "./partySelectorView.scss";
import { generateUrl } from "utils/generateUrl";

let cancelToken;
const { LOADING, SUCCESS, ERROR } = API_FETCH_STATUS;

const { party_selector_view, party_selector_filter_container, report_btn, btn_alignment } = styles;

const PARTY_SELECTOR_VIEWS = {
  DEFAULT: ({
    lob,
    product,
    lobDisplayName,
    selectedParty,
    setSelectedParty,
    partySelectorState,
    partyAggregationLevel,
    setPartySelectorState,
    viewMode,
  }) => {
    const { userPartyData, partyUniqueCount } = partySelectorState;

    return (
      <div>
        <PartyListContainer
          {...{
            lob,
            product,
            lobDisplayName,
            selectedParty,
            setSelectedParty,
            partySelectorState,
            partyAggregationLevel,
            setPartySelectorState,
            userPartyData: userPartyData?.[0],
            viewMode,
          }}
        />
      </div>
    );
  },
  ACCORDION: ({
    lob,
    product,
    lobDisplayName,
    selectedParty,
    setSelectedParty,
    partySelectorState,
    partyAggregationLevel,
    setPartySelectorState,
    partyUniqueCount,
    viewMode,
  }) => (
    <AccordionView
      {...{
        lob,
        product,
        lobDisplayName,
        selectedParty,
        setSelectedParty,
        partySelectorState,
        partyAggregationLevel,
        setPartySelectorState,
        viewMode,
      }}
    />
  ),
};

const getInitialState = (partyAggregationLevel) => {
  return { state: LOADING, viewMode: null, searchValue: "", searchType: getSearchTypeOptions(partyAggregationLevel)[0] };
};

const PartySelectorView = ({
  product,
  lobDetails,
  lobFromUrl,
  partyAggregationLevel,
  selectedParty,
  setSelectedParty,
  showRaiseSrButton,
  closePartySelector,
  source,
  user,
  onSelectAccount,
}) => {
  const history = useHistory();
  const [partySelectorState, setPartySelectorState] = useReducer(MergeStateReducer, getInitialState(partyAggregationLevel));
  const { fetchPartyAccounts } = usePartyAccounts();
  const { state, viewMode, searchValue, searchType } = partySelectorState;

  const isAtbSkam = user === PROFILE_TYPES.ATB_SECONDARY_KAM_USER;

  useEffect(() => {
    if (!lobFromUrl || (lobDetails && !isAtbSkam && (searchValue.length === 0 || searchValue.length > 2))) {
      fetchUserPartyData();
    }
  }, [lobDetails, searchValue]);

  const currentView = useMemo(() => {
    if (state === LOADING) {
      return <Loader />;
    }
    if (state === ERROR) {
      return (
        <WeAreSorry
          {...{
            buttonDisplayName: "Refresh",
            onButtonClick: () => {
              setPartySelectorState({ searchValue: "" });
            },
          }}
        />
      );
    }
    if (state === SUCCESS && viewMode) {
      return PARTY_SELECTOR_VIEWS[viewMode]({
        product,
        selectedParty,
        partySelectorState,
        partyAggregationLevel,
        setSelectedParty,
        setPartySelectorState,
        lob: lobDetails?.key,
        lobDisplayName: lobDetails?.displayName,
        viewMode,
      });
    }
    return null;
  }, [state, lobDetails, partySelectorState, selectedParty, viewMode]);

  const fetchUserPartyData = () => {
    if (searchValue.length === 0 || searchValue.length > 2) {
      setPartySelectorState({ state: LOADING });
    }
    fetchPartyAccounts({
      partyAggregationLevel,
      rowCount: { key: 10 },
      pageNo: 0,
      searchValue,
      lob: lobDetails?.key,
      product,
      searchType,
    })
      .then((response) => {
        setPartySelectorState({ ...response.data.data, state: SUCCESS });
      })
      .catch((error) => {
        if (!isRequestCancelled(error)) {
          setPartySelectorState({ state: ERROR });
          console.error(error);
        }
      });
  };

  const onRaiseSRClick = () => {
    const urlParams = encryptObject({
      issueCategory: RAISE_SR_ATB_ACCOUNT_ISSUE.ISSUE_CATEGORY.ATB_RELATED,
      dynamicSelectArray: [RAISE_SR_ATB_ACCOUNT_ISSUE.SUB_TYPE.ACCOUNT],
    });
    closePartySelector();
    const url = generateUrl(
      LINK_TO_ROUTE.SR_RAISE,
      { lob: lobDetails?.key },
      { partyId: selectedParty.partyId, params: urlParams },
    );
    navigateHandler({ history, source, path: url });
  };

  return (
    <div className={party_selector_view}>
      {isAtbSkam ? (
        <PartyInput
          {...{
            onSelectAccount,
            closePartySelector,
          }}
        />
      ) : (
        <>
          <div className={btn_alignment}>
            <div className={party_selector_filter_container}>
              <Filters {...{ product, partySelectorState, partyAggregationLevel, setPartySelectorState, setSelectedParty }} />
            </div>
            {showRaiseSrButton && (
              <div className={report_btn}>
                <Button onClick={onRaiseSRClick}>Report Issue</Button>
              </div>
            )}
          </div>
          {currentView}
        </>
      )}
    </div>
  );
};

export default PartySelectorView;
